import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import Modal from 'components/UI/Modal';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAuth } from 'lib/Auth';
import { createAttributesForBusinessUnitBulk } from 'requests/atribute';
import { ATTRIBUTE_NAME_REGEX, ATTRIBUTE_NAME_ERROR, ATTRIBUTE_MESSAGES, ATTRIBUTE_BUTTONS, RESTRICTED_ATTRIBUTE_NAMES, getRestrictedAttributeError } from 'constants/attributes';
import { Main } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { GENERAL_CONSTANTS } from 'constants/general';

const InputRow = styled(FlexboxContainer)`
  margin-bottom: ${Space.spBase};
  width: 100%;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${Main.error};
  cursor: pointer;
  font-size: 18px;
  padding: 0 8px;
  margin-left: 8px;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;


const AddMoreButton = styled(Button)`
  margin-top: ${Space.spQuarter};
`;

interface Props {
  showModal: boolean;
  onClose: () => void;
  businessUnitId: number;
  onSuccess: () => void;
  existingAttributeNames: string[];
}

const MAX_ATTRIBUTES = 25;
const MAX_ATTRIBUTE_NAME_LENGTH = '50';

const AddBulkAttributesModal = ({ showModal, onClose, businessUnitId, onSuccess, existingAttributeNames }: Props) => {
  const [attributeNames, setAttributeNames] = useState<string[]>(['']);
  const [errors, setErrors] = useState<boolean[]>([false]);
  const [errorMessages, setErrorMessages] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token } = useAuth();

  const resetState = () => {
    setAttributeNames(['']);
    setErrors([false]);
    setErrorMessages(['']);
    setIsSubmitting(false);
  };

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateName = (name: string): boolean => {
    if (!name.trim()) return false; // Empty/whitespace-only names are invalid
    return ATTRIBUTE_NAME_REGEX.test(name);
  };

  const checkIfAttributeExists = (name: string): boolean => {
    const trimmedName = name.trim().toLowerCase();
    return existingAttributeNames.some(
      existingName => existingName.toLowerCase() === trimmedName
    );
  };

  const checkForDuplicates = (names: string[]): number[] => {
    const duplicateIndices: number[] = [];
    const trimmedNames = names.map(n => n.trim().toLowerCase());

    trimmedNames.forEach((name, index) => {
      if (name && trimmedNames.indexOf(name) !== index) {
        duplicateIndices.push(index);
        // Also mark the first occurrence
        const firstIndex = trimmedNames.indexOf(name);
        if (!duplicateIndices.includes(firstIndex)) {
          duplicateIndices.push(firstIndex);
        }
      }
    });

    return duplicateIndices;
  };

  const handleInputChange = (index: number, value: string) => {
    const newNames = [...attributeNames];
    newNames[index] = value;
    setAttributeNames(newNames);

    // Clear error for this input while typing
    const newErrors = [...errors];
    const newErrorMessages = [...errorMessages];
    newErrors[index] = false;
    newErrorMessages[index] = '';
    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
  };

  const handleBlur = (index: number) => {
    const name = attributeNames[index];
    const newErrors = [...errors];
    const newErrorMessages = [...errorMessages];

    // First, clear all duplicate and existing attribute errors to re-evaluate
    newErrors.forEach((error, i) => {
      if (errorMessages[i] === ATTRIBUTE_MESSAGES.DUPLICATE_ATTRIBUTE_NAMES ||
          errorMessages[i] === ATTRIBUTE_MESSAGES.ATTRIBUTE_ALREADY_EXISTS) {
        newErrors[i] = false;
        newErrorMessages[i] = '';
      }
    });

    // Check for empty/whitespace-only names
    if (!name.trim()) {
      newErrors[index] = true;
      newErrorMessages[index] = ATTRIBUTE_MESSAGES.ATTRIBUTE_NAME_REQUIRED;
    }
    // Validate regex for current field
    else if (!validateName(name)) {
      newErrors[index] = true;
      newErrorMessages[index] = ATTRIBUTE_NAME_ERROR;
    }
    // Check if attribute name is restricted (case-insensitive)
    else if (RESTRICTED_ATTRIBUTE_NAMES.includes(name.trim().toLowerCase())) {
      newErrors[index] = true;
      newErrorMessages[index] = getRestrictedAttributeError(name.trim());
    }
    // Check if attribute already exists
    else if (checkIfAttributeExists(name)) {
      newErrors[index] = true;
      newErrorMessages[index] = ATTRIBUTE_MESSAGES.ATTRIBUTE_ALREADY_EXISTS;
    }

    // Check for duplicates across all fields
    const duplicateIndices = checkForDuplicates(attributeNames);
    duplicateIndices.forEach(i => {
      // Only set duplicate error if there's no higher-priority validation error
      if (!newErrors[i]) {
        newErrors[i] = true;
        newErrorMessages[i] = ATTRIBUTE_MESSAGES.DUPLICATE_ATTRIBUTE_NAMES;
      }
    });

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
  };

  const handleAddMore = () => {
    if (attributeNames.length < MAX_ATTRIBUTES) {
      setAttributeNames([...attributeNames, '']);
      setErrors([...errors, false]);
      setErrorMessages([...errorMessages, '']);
    }
  };

  const handleRemove = (index: number) => {
    if (attributeNames.length > 1) {
      const newNames = attributeNames.filter((_, i) => i !== index);
      const newErrors = errors.filter((_, i) => i !== index);
      const newErrorMessages = errorMessages.filter((_, i) => i !== index);
      setAttributeNames(newNames);
      setErrors(newErrors);
      setErrorMessages(newErrorMessages);
    }
  };

  const handleSave = async () => {
    // Final validation - validate ALL inputs including empty ones
    const newErrors = attributeNames.map((name) => {
      // Check for empty or whitespace-only names
      if (!name.trim()) return true;
      if (!validateName(name)) return true;
      // Check restricted names
      if (RESTRICTED_ATTRIBUTE_NAMES.includes(name.trim().toLowerCase())) return true;
      // Check if attribute already exists
      if (checkIfAttributeExists(name)) return true;
      return false;
    });

    const newErrorMessages: string[] = attributeNames.map((name) => {
      // Check for empty or whitespace-only names
      if (!name.trim()) return ATTRIBUTE_MESSAGES.ATTRIBUTE_NAME_REQUIRED;
      if (!validateName(name)) return ATTRIBUTE_NAME_ERROR;
      // Check restricted names
      if (RESTRICTED_ATTRIBUTE_NAMES.includes(name.trim().toLowerCase())) {
        return getRestrictedAttributeError(name.trim());
      }
      // Check if attribute already exists
      if (checkIfAttributeExists(name)) return ATTRIBUTE_MESSAGES.ATTRIBUTE_ALREADY_EXISTS;
      return '';
    });

    // Check duplicates
    const duplicateIndices = checkForDuplicates(attributeNames);
    duplicateIndices.forEach(i => {
      // Only set duplicate error if no other validation error exists
      if (!newErrors[i]) {
        newErrors[i] = true;
        newErrorMessages[i] = ATTRIBUTE_MESSAGES.DUPLICATE_ATTRIBUTE_NAMES;
      }
    });

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);

    if (newErrors.some(e => e)) {
      return;
    }

    // All inputs are valid at this point
    const validNames = attributeNames.map(name => name.trim());

    setIsSubmitting(true);

    try {
      await createAttributesForBusinessUnitBulk(validNames, businessUnitId, token!);
      renderToast(toast.TYPE.SUCCESS, ATTRIBUTE_MESSAGES.ATTRIBUTES_CREATED_SUCCESS);
      resetState();
      onSuccess();
      onClose();
    } catch (err: any) {
      renderToast(toast.TYPE.ERROR, err.message || ATTRIBUTE_MESSAGES.ATTRIBUTES_CREATION_FAILED);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasAnyErrors = errors.some(e => e);
  const allEmpty = attributeNames.every(name => name.trim() === '');
  const cantSubmit = hasAnyErrors || allEmpty || !token || isSubmitting;

  return (
    <Modal
      showModal={showModal}
      showCloseIcon
      onClose={handleClose}
      onClick={handleSave}
      disabled={cantSubmit}
      title="Add attribute"
      primaryButtonText="Save"
      ghostButtonText="Cancel"
    >
      <FlexboxContainer flexDirection="column" width="100%">
        {attributeNames.map((name, index) => (
          <InputRow key={index} alignItems="center">
            <FlexboxContainer width="100%" flexDirection="column">
              <Input
                id={`bulk-attribute-name-${index}`}
                placeholder="Attribute name"
                labelName={index === 0 ? "Name" : undefined}
                labelHidden={index !== 0}
                onInputChange={(value) => handleInputChange(index, value)}
                onBlur={() => handleBlur(index)}
                value={name}
                isInvalid={errors[index]}
                isOptional
                choiceOptional
                showOptional={false}
                errorMessage={errorMessages[index]}
                required
                displayAsterisk
                maxLength={MAX_ATTRIBUTE_NAME_LENGTH}
              />
            </FlexboxContainer>
            {attributeNames.length > 1 && (
              <RemoveButton
                type="button"
                onClick={() => handleRemove(index)}
                disabled={isSubmitting}
                title={GENERAL_CONSTANTS.REMOVE}
              >
                ✕
              </RemoveButton>
            )}
          </InputRow>
        ))}

        <AddMoreButton
          onClick={handleAddMore}
          disabled={attributeNames.length >= MAX_ATTRIBUTES || isSubmitting}
          color={Main.accent}
          isGhost
          bgColor='transparent'
          borderColor='transparent'
        >
          {ATTRIBUTE_BUTTONS.ADD_MORE}
        </AddMoreButton>

        {attributeNames.length >= MAX_ATTRIBUTES && (
          <FlexboxContainer margin="8px 0 0 0">
            <span style={{ color: Main.warning, fontSize: '12px' }}>
              {ATTRIBUTE_MESSAGES.MAX_ATTRIBUTES_EXCEEDED}
            </span>
          </FlexboxContainer>
        )}
      </FlexboxContainer>
    </Modal>
  );
};

export default AddBulkAttributesModal;
